import React, { Component } from 'react';
import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Table,
  FormGroup,
  Input,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Button,
  Alert,
} from 'reactstrap';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import normalizeFilterText from 'lib/normalizeFilterText.js';
import { consumesTemplates, providesTemplates } from 'contexts/TemplatesContext';
import translate from 'containers/translate';
import VatSelect from 'components/vatSelect/VatSelect';
import { consumesDossier } from 'contexts/DossierContext';
import { providesUsedCompanies } from 'contexts/UsedCompaniesContext';
import TemplateListEntry from 'components/templateListEntry/TemplateListEntry';
import withCompanies from 'containers/withCompanies';
import './TemplateSelect.scss';

class FilterInput extends Component {
  focus() {
    this.inputRef.focus();
  }

  render() {
    const { value, onChange, onKeyPress, placeholder } = this.props;
    return (
      <Input
        innerRef={(ref) => (this.inputRef = ref)}
        type="search"
        placeholder={placeholder}
        value={value}
        onKeyPress={onKeyPress}
        onChange={onChange}
      />
    );
  }
}

FilterInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onKeyPress: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
};

class TemporaryTemplate extends Component {
  constructor(props) {
    super(props);
    this.state = {
      template: {
        company: {
          name: 'Unbekannte Firma',
        },
        account: '1000',
        accountingText: '',
        vat: 0,
        vatType: 'NORMAL',
      },
    };
  }

  handleTemplateChange = (template) => {
    this.setState({ template });
  };

  render() {
    const { t, onApply, dossier, companyNames } = this.props;
    const { template } = this.state;

    return (
      <div className="mt-3">
        <Alert color="info">{t('temporary_template_alert')}</Alert>
        <Table>
          <thead>
            <tr>
              <th>{t('company')}</th>
              <th>{t('account')}</th>
              <th>{t('vat')}</th>
              <th>{t('accounting_text')}</th>
            </tr>
          </thead>
          <tbody>
            <TemplateListEntry
              template={template}
              dossier={dossier}
              companyNames={companyNames}
              usedCompanyNames={[]}
              locked={false}
              onTemplateChange={this.handleTemplateChange}
              onTemplateDelete={() => {}}
              temporary={true}
            />
          </tbody>
        </Table>
        <ModalFooter>
          <Button color="primary" onClick={() => onApply(template)}>
            {t('apply_template')}
          </Button>
        </ModalFooter>
      </div>
    );
  }
}

TemporaryTemplate.propTypes = {
  t: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  dossier: PropTypes.object.isRequired,
  companyNames: PropTypes.arrayOf(PropTypes.string).isRequired,
};

const TemporaryTemplateWithCompanies = providesUsedCompanies(withCompanies(TemporaryTemplate));

class TemplateSelect extends Component {
  constructor() {
    super();

    this.state = {
      filter: '',
      activeTab: 'saved',
    };

    this.handleFilterChange = this.handleFilterChange.bind(this);
    this.handleInputKeyPress = this.handleInputKeyPress.bind(this);
    this.filterInputRef = React.createRef();
  }

  handleModalOpened = () => {
    if (this.filterInputRef.current) {
      this.filterInputRef.current.focus();
    }
  };

  getFilteredTemplates() {
    if (!this.state.filter) {
      return this.props.templates;
    }

    const normalizedFilter = normalizeFilterText(this.state.filter);

    return this.props.templates.filter((t) => {
      const textToSearch = normalizeFilterText([t.name, t.account, t.accountingText, t.company.name].join(' '));
      return textToSearch.includes(normalizedFilter);
    });
  }

  handleFilterChange(e) {
    this.setState({
      filter: e.target.value,
    });
  }

  handleInputKeyPress(e) {
    if (e.key === 'Enter') {
      const filteredTemplates = this.getFilteredTemplates();

      if (filteredTemplates.length === 1) {
        this.props.onDismiss(filteredTemplates[0]);
      }
    }
  }

  renderTemplates() {
    const filteredTemplates = this.getFilteredTemplates();

    return filteredTemplates.map((template) => {
      return (
        <tr key={template.id} onClick={() => this.props.onDismiss(template)}>
          <td>{template.name}</td>
          <td>{template.company.name}</td>
          <td>{template.account}</td>
          <td>
            <VatSelect
              presetVat={template.vat}
              presetVatType={template.vatType}
              dossier={this.props.dossier}
              locked={true}
              onVatChange={() => {}}
            />
          </td>
          <td>{template.accountingText}</td>
        </tr>
      );
    });
  }

  render() {
    const { t } = this.props;
    const { activeTab } = this.state;

    return (
      <Modal
        isOpen
        size="lg"
        className="TemplateSelect"
        toggle={() => this.props.onDismiss()}
        onOpened={this.handleModalOpened}
      >
        <ModalHeader toggle={() => this.props.onDismiss()}>{t('templates')}</ModalHeader>
        <ModalBody>
          <Nav tabs>
            <NavItem>
              <NavLink
                tag="span"
                className={classNames({ active: activeTab === 'saved' })}
                style={{ cursor: 'pointer' }}
                onClick={() => this.setState({ activeTab: 'saved' })}
              >
                {t('saved_templates')}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                tag="span"
                className={classNames({ active: activeTab === 'temporary' })}
                style={{ cursor: 'pointer' }}
                onClick={() => this.setState({ activeTab: 'temporary' })}
              >
                {t('temporary_template')}
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeTab} className="mt-4">
            <TabPane tabId="saved">
              <FormGroup>
                <FilterInput
                  ref={this.filterInputRef}
                  value={this.state.filter}
                  onChange={this.handleFilterChange}
                  onKeyPress={this.handleInputKeyPress}
                  placeholder={t('filter')}
                />
              </FormGroup>

              <Table>
                <thead>
                  <tr>
                    <th>{t('name')}</th>
                    <th>{t('company')}</th>
                    <th>{t('account')}</th>
                    <th>{t('vat')}</th>
                    <th>{t('accounting_text')}</th>
                  </tr>
                </thead>
                <tbody className="template-list">
                  {this.state.filter ? null : (
                    <tr>
                      <td colSpan="5" onClick={() => this.props.onDismiss('DEFAULT')}>
                        {t('default_template')}
                      </td>
                    </tr>
                  )}
                  {this.renderTemplates()}
                </tbody>
              </Table>
            </TabPane>
            <TabPane tabId="temporary">
              <TemporaryTemplateWithCompanies t={t} onApply={this.props.onDismiss} dossier={this.props.dossier} />
            </TabPane>
          </TabContent>
        </ModalBody>
      </Modal>
    );
  }
}

TemplateSelect.propTypes = {
  t: PropTypes.func.isRequired,
  dossier: PropTypes.object.isRequired,
  templates: PropTypes.arrayOf(PropTypes.object).isRequired,
  onDismiss: PropTypes.func.isRequired,
};

export default providesTemplates(consumesDossier(consumesTemplates(translate(TemplateSelect))));
